import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, BookOpen, Image as ImageIcon, Check, Loader2, ChevronRight, Plus } from 'lucide-react';
import { getDeviceImages, DeviceImageMetadata } from '../services/deviceImages';
import { sessionTasksService } from '../services/sessionTasksService';
import type { ExtractedPersona } from '../services/workshop';

interface ImageSlots {
  cover: string | null;
  challenge: string | null;
  personas: Record<string, string>;
  happyPath: string | null;
  additionalPages: string[];
}

interface WorkshopBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  personas: ExtractedPersona[];
  onTaskCreated: (workflowId: string, executionId: string) => void;
}

// ============================================================================
// IMAGE SLOT PICKER
// ============================================================================

const ImageSlotPicker: React.FC<{
  label: string;
  selectedKey: string | null;
  images: DeviceImageMetadata[];
  onSelect: (s3Key: string | null) => void;
}> = ({ label, selectedKey, images, onSelect }) => {
  const [open, setOpen] = useState(false);
  const selected = images.find(img => img.s3Key === selectedKey);

  return (
    <div className="mb-4">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{label}</div>
      {open ? (
        <div>
          <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto mb-1.5">
            <button
              onClick={() => { onSelect(null); setOpen(false); }}
              className="aspect-square rounded border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 hover:border-slate-400 text-xs"
            >
              None
            </button>
            {images.map(img => (
              <button
                key={img.s3Key}
                onClick={() => { onSelect(img.s3Key); setOpen(false); }}
                className={`relative aspect-square rounded overflow-hidden border-2 transition-all ${
                  img.s3Key === selectedKey ? 'border-teal-500' : 'border-transparent hover:border-slate-300'
                }`}
              >
                <img src={img.presignedUrl} alt="" className="w-full h-full object-cover" />
                {img.s3Key === selectedKey && (
                  <div className="absolute inset-0 bg-teal-500/20 flex items-center justify-center">
                    <Check size={16} className="text-teal-600" />
                  </div>
                )}
              </button>
            ))}
          </div>
          <button onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-slate-700">
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className={`w-full flex items-center gap-2 rounded-lg border p-2 text-sm transition-all hover:border-slate-400 ${
            selected ? 'border-slate-300 bg-white' : 'border-dashed border-slate-300 bg-slate-50'
          }`}
        >
          {selected ? (
            <>
              <img src={selected.presignedUrl} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
              <span className="text-slate-700 text-xs truncate flex-1 text-left">{selected.captureLabel || selected.imageId}</span>
              <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
            </>
          ) : (
            <>
              <ImageIcon size={14} className="text-slate-400 flex-shrink-0" />
              <span className="text-slate-400 flex-1 text-left">Select from gallery</span>
              <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
            </>
          )}
        </button>
      )}
    </div>
  );
};

// ============================================================================
// MAIN MODAL
// ============================================================================

const WorkshopBookModal: React.FC<WorkshopBookModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  personas,
  onTaskCreated,
}) => {
  const [images, setImages] = useState<DeviceImageMetadata[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);
  const [imageSlots, setImageSlots] = useState<ImageSlots>({
    cover: null,
    challenge: null,
    personas: {},
    happyPath: null,
    additionalPages: [],
  });
  // Stable React keys for additionalPages slots, decoupled from array position
  const [additionalPageIds, setAdditionalPageIds] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wasOpenRef = useRef(false);
  const personasRef = useRef(personas);
  personasRef.current = personas;

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      setSelectedPersonaIds(personasRef.current.map(p => p.id));
      setImageSlots({ cover: null, challenge: null, personas: {}, happyPath: null, additionalPages: [] });
      setAdditionalPageIds([]);
      setError(null);
      setLoadingImages(true);
      getDeviceImages(sessionId)
        .then(setImages)
        .catch(() => setImages([]))
        .finally(() => setLoadingImages(false));
    } else if (!isOpen) {
      wasOpenRef.current = false;
    }
  }, [isOpen, sessionId]);

  const togglePersona = useCallback((id: string) => {
    setSelectedPersonaIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  const setPersonaImage = useCallback((personaId: string, s3Key: string | null) => {
    setImageSlots(prev => ({
      ...prev,
      personas: s3Key
        ? { ...prev.personas, [personaId]: s3Key }
        : Object.fromEntries(Object.entries(prev.personas).filter(([k]) => k !== personaId)),
    }));
  }, []);

  const allSlotsFilledForSelected = () => {
    for (const id of selectedPersonaIds) {
      if (!imageSlots.personas[id]) return false;
    }
    return true;
  };

  const canGenerate =
    selectedPersonaIds.length > 0 &&
    imageSlots.cover &&
    imageSlots.challenge &&
    imageSlots.happyPath &&
    allSlotsFilledForSelected();

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await sessionTasksService.createTask(sessionId, {
        taskTemplateId: 'session-task-generate-workshop-book',
        taskName: 'Workshop Book',
        taskCategory: 'doing',
        contextSnapshot: {
          customContext: {
            selectedPersonaIds,
            imageSlots,
          },
        },
      });
      onTaskCreated(result.workflowId, result.executionId);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to start book generation');
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <BookOpen size={18} className="text-teal-500" />
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Generate Workshop Book</h2>
              <p className="text-xs text-slate-500">Assign images, select personas, then generate</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loadingImages && (
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
              <Loader2 size={14} className="animate-spin" /> Loading session images…
            </div>
          )}

          {images.length === 0 && !loadingImages && (
            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              No images found in this session. Upload images to the Images tab first, then return here to assign them.
            </div>
          )}

          {/* Global image slots */}
          <div className="mb-5">
            <div className="text-sm font-semibold text-slate-800 mb-3">Book Images</div>
            <ImageSlotPicker
              label="Cover image"
              selectedKey={imageSlots.cover}
              images={images}
              onSelect={key => setImageSlots(prev => ({ ...prev, cover: key }))}
            />
            <ImageSlotPicker
              label="Challenge image (full bleed page 2)"
              selectedKey={imageSlots.challenge}
              images={images}
              onSelect={key => setImageSlots(prev => ({ ...prev, challenge: key }))}
            />
            <ImageSlotPicker
              label="Future happy path image"
              selectedKey={imageSlots.happyPath}
              images={images}
              onSelect={key => setImageSlots(prev => ({ ...prev, happyPath: key }))}
            />
          </div>

          {/* Persona selection */}
          <div className="mb-5">
            <div className="text-sm font-semibold text-slate-800 mb-1">Personas to Include</div>
            <div className="text-xs text-slate-500 mb-3">Each selected persona gets a full-bleed image page + stories page.</div>
            {personas.length === 0 ? (
              <div className="text-xs text-slate-500">No personas found in this session.</div>
            ) : (
              <div className="space-y-3">
                {personas.map(persona => {
                  const included = selectedPersonaIds.includes(persona.id);
                  return (
                    <div key={persona.id} className={`rounded-lg border p-3 transition-all ${included ? 'border-teal-200 bg-teal-50/50' : 'border-slate-200 bg-white'}`}>
                      <div className="flex items-start gap-2.5 mb-2">
                        <button
                          onClick={() => togglePersona(persona.id)}
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-all ${
                            included ? 'bg-teal-500 border-teal-500' : 'border-slate-300'
                          }`}
                        >
                          {included && <Check size={10} className="text-white" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-800">{persona.name}</div>
                          <div className="text-xs text-slate-500">{persona.role}</div>
                        </div>
                      </div>
                      {included && (
                        <div className="ml-6">
                          <ImageSlotPicker
                            label={`${persona.name} image`}
                            selectedKey={imageSlots.personas[persona.id] || null}
                            images={images}
                            onSelect={key => setPersonaImage(persona.id, key)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Additional full-page images */}
          <div className="mb-5">
            <div className="text-sm font-semibold text-slate-800 mb-1">Additional Images</div>
            <div className="text-xs text-slate-500 mb-3">Full-bleed pages inserted after personas, before the happy path.</div>
            {imageSlots.additionalPages
              .map((pageKey, idx) => ({ pageKey, idx, id: additionalPageIds[idx] ?? `page-slot-fallback-${pageKey}-${idx}` }))
              .map(({ pageKey, idx, id }) => (
              <div key={id} className="flex items-start gap-2">
                <div className="flex-1">
                  <ImageSlotPicker
                    label={`Image ${idx + 1}`}
                    selectedKey={pageKey || null}
                    images={images}
                    onSelect={newKey => {
                      const updated = [...imageSlots.additionalPages];
                      const updatedIds = [...additionalPageIds];
                      if (newKey) { updated[idx] = newKey; } else { updated.splice(idx, 1); updatedIds.splice(idx, 1); }
                      setImageSlots(prev => ({ ...prev, additionalPages: updated }));
                      setAdditionalPageIds(updatedIds);
                    }}
                  />
                </div>
                <button
                  onClick={() => {
                    setImageSlots({ ...imageSlots, additionalPages: imageSlots.additionalPages.filter((_, i) => i !== idx) });
                    setAdditionalPageIds(additionalPageIds.filter((_, i) => i !== idx));
                  }}
                  className="mt-1 p-1.5 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                setImageSlots(prev => ({ ...prev, additionalPages: [...prev.additionalPages, ''] }));
                setAdditionalPageIds(prev => [...prev, crypto.randomUUID()]);
              }}
              className="flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-700 font-medium mt-1 transition-colors"
            >
              <Plus size={12} /> Add image
            </button>
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {!canGenerate && 'Fill all image slots and select at least one persona to continue'}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={!canGenerate || generating}
                className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <><Loader2 size={14} className="animate-spin" /> Generating…</>
                ) : (
                  <><BookOpen size={14} /> Generate Book</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkshopBookModal;
